import { Injectable, inject, signal, computed } from '@angular/core';
import { Params, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserInterface } from '@app/interfaces/user.interface';
import { SnackbarService } from './snackbar.service';
import { ApiService } from '@app/services/api.service';
import { MixpanelService } from '@app/services/mixpanel.service';
import { BridgeService } from '@app/services/bridge.service';
import { FingerprintService } from '@app/services/fingerprint.service';
import { Id } from '@feathersjs/feathers';
import { UserContactInterface } from '@app/interfaces/user-contact.interface';
import jwt_decode from 'jwt-decode';

export interface LoginResponse {
    authenticated: boolean;
    errorCode?: number;
    errorMessage?: string;
    throttle?: number;
}

@Injectable({ providedIn: 'root' })
export class UsersService extends ApiService<UserInterface> {
    private readonly router = inject(Router);
    private readonly snackbarService = inject(SnackbarService);
    private readonly mixpanelService = inject(MixpanelService);
    private readonly bridgeService = inject(BridgeService);
    private readonly fingerprintService = inject(FingerprintService);

    private readonly _currentUser = signal<UserInterface>(null);
    readonly currentUser = this._currentUser.asReadonly();

    private readonly _authenticated = signal<boolean>(false);
    readonly authenticated = this._authenticated.asReadonly();

    readonly currentUserEmail = computed(() => this._currentUser()?.contact?.email);
    readonly emailVerified = computed(() => this._currentUser()?.contact?.emailVerified ?? false);
    readonly activated = computed(() => this._currentUser()?.isActivated ?? false);

    public accessToken = null;

    public user: UserInterface = null;
    private $user: BehaviorSubject<UserInterface> = new BehaviorSubject(null);
    public user$: Observable<UserInterface> = this.$user.asObservable();

    // @API users
    protected serviceName = 'users';

    constructor() {
        super();
        this.initConnection();
        this.backendService.connected$.subscribe(async (connected) => {
            if (connected) {
                await this.checkLogin();
            } else {
                this.setAuthenticated(false);
            }
        });

        this.backendService.setupHooks({
            error: {
                all: [
                    async (context) => {
                        if (context.error?.code === 401) {
                            const auth = context.error.hook?.path === 'authentication';
                            if (!auth) {
                                this.logout(auth).then(() => {
                                    if (!auth) {
                                        this.snackbarService.showError(
                                            'Vypršela platnost přihlášení. Z bezpečnostních důvodů jste byli odhlášeni.'
                                        );
                                    }
                                });
                            }
                        }
                        return context;
                    },
                ],
            },
        });
    }

    private setAuthenticated(authenticated: boolean) {
        if (this.authenticated() !== authenticated) {
            this._authenticated.set(authenticated);
        }
    }

    public setActivated(isActivated: boolean) {
        this._currentUser.set({ ...this._currentUser(), isActivated });
        this.user.isActivated = isActivated;
        this.$user.next(this.user);
    }

    public async loginPasswordLessByUserId(userId: string, token: string, code: string): Promise<LoginResponse> {
        try {
            // await this.logout(true);
            const visitorId = await this.fingerprintService.getFingerPrint();
            const authenticated = await this._authenticatePasswordLess({
                userId,
                token,
                code,
                visitorId,
            });
            return { authenticated };
        } catch (error: any) {
            let message = 'Nyní se nelze přihlásit. Zkuste to později.';
            if (error.code === 429) {
                message = 'Váš účet je zablokován. ';
            } else if (error.code === 401) {
                message = 'Špatné přihlašovací údaje.';
                if (error.message === 'Token is no longer valid!' || error.message === 'Token usage exceeded!') {
                    message = 'Vypršela platnost přihlášení. Přihlašte se prosím znovu.';
                    this.snackbarService.showError(message);
                }
                if (error.message === 'Wrong credentials!') {
                    message = 'Chybný ověřovací kód';
                }
            }
            return {
                authenticated: false,
                errorCode: error.code,
                throttle: error.data?.retryAfter,
                errorMessage: message,
            };
        }
    }

    public async logout(noRedirect: boolean = false): Promise<void> {
        try {
            if (this.authenticated()) {
                await this.backendService.logout();
            }
            if (!noRedirect) {
                await this.router.navigate(['/login']);
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.setAuthenticated(false);
            this.setCurrentUser(null);
            this.mixpanelService.reset();
            this.bridgeService.platformUserId = null;
            this.bridgeService.activateEmail = false;
        }
    }

    public async checkLogin(): Promise<boolean> {
        try {
            const res = await this.backendService.reAuthenticate();
            this.accessToken = res.accessToken;
            const user = res.user;
            if (!this.user) {
                this.setCurrentUser(user);
            }
            if (user.role === 'user') {
                this.setAuthenticated(true);
            } else {
                this.setAuthenticated(false);
            }
            return this.authenticated();
        } catch {
            this.setAuthenticated(false);
            return this.authenticated();
        }
    }

    public isTokenExpired(): boolean {
        const accessToken = localStorage.getItem('feathers-jwt');
        if (!accessToken) {
            return true;
        }

        const token: any = jwt_decode(accessToken);
        const now = Date.now();
        return now > token.exp * 1000;
    }

    public clearUserData(): void {
        if (this.backendService.lsIsAvailable) {
            localStorage.removeItem('feathers-jwt');
        }
    }

    public setCurrentUser(user: UserInterface) {
        console.log('UsersService-setCurrentUser', user);
        this.user = user;
        this._currentUser.set(user);
        this.$user.next(user);
    }

    async authenticateJwt(jwt: string): Promise<boolean> {
        try {
            const response = await this.backendService.authenticate({
                strategy: 'jwt',
                accessToken: jwt,
            });

            this.accessToken = response.accessToken;
            const user = response.user;
            this.setCurrentUser(user);

            const isUserRole = user.role === 'user';
            this.setAuthenticated(isUserRole);

            return this.authenticated();
        } catch (error: any) {
            this.setAuthenticated(false);
            throw error;
        }
    }

    protected _updated(updatedItem: UserInterface): void {
        console.log('UsersService._updated', updatedItem); //TODO REMOVE
        if (this.user && this.user._id === updatedItem._id) {
            this.setCurrentUser(updatedItem);
        }
    }

    private async _authenticatePasswordLess(params: {
        userId: string;
        token: string;
        code: string;
        visitorId: string;
    }): Promise<boolean> {
        try {
            const response = await this.backendService.authenticate({
                strategy: 'local-passwordless',
                ...params,
            });
            this.accessToken = response.accessToken;
            const user = response.user;
            this.setCurrentUser(user);
            if (user.role === 'user') {
                this.setAuthenticated(true);
            } else {
                this.setAuthenticated(false);
            }
            return this.authenticated();
        } catch (error: any) {
            this.setAuthenticated(false);
            throw error;
        }
    }

    // patches users and also contacts
    public patchUserContact(id: Id, data: Partial<UserContactInterface>, params?: Params): Promise<any> {
        this.checkSocket();
        return this.backendService.getService('users').patch(id, data, params);
    }
}
